import { DeleteAction, EditAction } from "@/components/Action";
import { ListCountry } from "@/constants/country";
import { DATE_FORMAT } from "@/constants/default";
import { EnumTypeTa } from "@/constants/enum";
import { usePermissions } from "@/rbac";
import { permissions } from "@/rbac/permissions";
import { Rental } from "@/types/rental.type";
import { Tenant } from "@/types/tenant.type";
import { formatDateTimeAutoToString } from "@/utils/format";
import { classNameExpiredRental, classNameTenantFinExpiry, showStatusRental } from "@/utils/helper";
import { ColumnDef } from "@tanstack/react-table";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

export default function TableHeader(): ColumnDef<Rental>[] {
    const {t} = useTranslation();
    const { hasPermission } = usePermissions();
    const navigate = useNavigate()
    const location = useLocation()

    return [
        {
            header: t("rental.business_unit"),
            accessorKey: "business_unit_name",
            cell: ({row}) => (
                <div className="cursor-pointer" onClick={() => navigate(location , {state: {modal: "/rentals/property", id: row.original.business_unit_id}})}>{row.original.business_unit_name}</div>
            )
        },
        {
            header: t("rental.room"),
            accessorKey: "room_code",
        },
        {
            header: t("tenant.ta_ref_no"),
            accessorKey: "ta_ref_no"
        },
        {
            header: t("rental.agent_name"),
            accessorKey: "agent_name",
        },
        {
            header: () => {
                return <div className="flex">
                    <p className="w-1/2 capitalize">{t("rental.tenants")}</p>
                    <p className="w-1/4 capitalize">{t("tenant.nationality")}</p>
                    <p className="w-1/4 ps-1 capitalize">{t("tenant.fin_expiry")}</p>
                </div>
            },
            accessorKey: "tenant_name",
            enableSorting: false,
            meta: {
                className: 'w-96 py-0 px-0',
            },
            cell: ({row}) => {
                const data = JSON.parse(row.original.tenants || '[]') as Tenant[]
                return <table className="w-full table-auto">
                    <tbody>
                        {
                            data.map((tenant, index) => (
                                <tr key={tenant.id} className={`py-2 px-2 border-${index < data.length - 1 ? 'b' : '0'} dark:border-slate-400`}>
                                    <td className="py-2 px-2 w-6/12 dark:text-white">{tenant.name}</td>
                                    <td className="w-3/12 dark:text-white">{ListCountry().find(c => c.value === tenant.nationality)?.label || ""}</td>
                                    <td className="w-3/12 dark:text-white">{
                                        tenant.type >= EnumTypeTa.NORMAL  && <div className={`ps-1 py-2 dark:text-white ${classNameTenantFinExpiry(tenant.fin_expiry)}`}>
                                            {
                                                tenant.fin_expiry ? formatDateTimeAutoToString(tenant.fin_expiry ?? '', "ll") : "N/A"
                                            }
                                        </div>
                                    }</td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            }
        },
        {
            header: t("rental.start_date"),
            accessorKey: "start_date",
            enableSorting: true,
            cell: ({row}) => formatDateTimeAutoToString(row.original.start_date ?? '', "ll")
        },
        {
            header: t("rental.end_date"),
            accessorKey: "end_date",
            enableSorting: true,
            meta: {
                className: "!px-0 !py-0 min-w-26 w-26"
            },
            cell: ({row}) => <div className={`py-2 px-2 dark:text-white ${classNameExpiredRental(row.original.status, row.original.end_date)}`}>
                {
                    formatDateTimeAutoToString(row.original.end_date ?? '', "ll")
                }
            </div>
        },
        {
            header: t("rental.rent_amount"),
            accessorKey: "rent_amount",
            cell: ({row}) => {
                return row.original.rent_amount > 0 && <span>${row.original.rent_amount.toFixed(2)}</span>
            }
        },
        {
            header: t("rental.deposit_amount"),
            accessorKey: "deposit_amount",
            cell: ({row}) => {
                return row.original.deposit_amount > 0 && <span>${row.original.deposit_amount.toFixed(2)}</span>
            }
        },
        {
            header: t("rental.deposit_date"),
            accessorKey: "deposit_date",
            cell: ({row}) => formatDateTimeAutoToString(row.original.deposit_date ?? '', "ll")
        },
        
        // {
        //     header: t("rental.late_fee"),
        //     accessorKey: "late_fee",
        //     enableSorting: false,
        //     cell: ({row}) => row.original.late_fee > 0 && <span>${row.original.late_fee.toFixed(2)}</span>
        // },
        // {
        //     header: t("rental.late_grace"),
        //     accessorKey: "late_grace",
        //     enableSorting: false,
        //     cell: ({row}) => row.original.late_grace > 0 && row.original.late_grace
        // },
        // {
        //     header: t("table.created_at"),
        //     accessorKey: "created_at",
        //     cell: ({ row }) => formatDateTimeAutoToString(row.original.created_at ?? '', DATE_FORMAT)
        // },
        {
            header: t("table.status"),
            accessorKey: "status",
            meta: {
                className: "min-w-32 w-32"
            },
            enableSorting: false,
            cell: ({ row }) => <div dangerouslySetInnerHTML={{__html: showStatusRental(row.original.status, row.original.start_date, row.original.end_date)}} />
        },
        {
            header: t("table.action"),
            accessorKey: "action",
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex items-center space-x-3.5">
                    {
                        hasPermission(permissions.RENTAL.EDIT) && <EditAction onClick={() => navigate(location , {state: {modal: "/rentals/edit", id: row.original.id}})} />
                    }
                    {
                        hasPermission(permissions.RENTAL.DELETE) && <DeleteAction onClick={() => navigate(location , {state: {modal: "/rentals/delete", id: row.original.id}})} />
                    }
                </div>
            )
        }
    ]
}